'use client'

import { useCreateContactMutation } from '@/api'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { zodResolver } from '@hookform/resolvers/zod'
import parse from 'html-react-parser'
import { useRouter } from 'next/navigation'
import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import TEXT_DATA from '@/data/privacy_policy.json'

interface DATA_TYPE {
  title: string
  text: string
}

const schema = z.object({
  name: z.string().min(1, '必須項目です'),
  email: z
    .string()
    .min(1, '必須項目です')
    .email('メールアドレスの形式が正しくありません'),
  tel: z.string().min(1, '必須項目です'),
  message: z.string().min(1, '必須項目です'),
})

type ContactFormData = z.infer<typeof schema>

const ContactPage = () => {
  const router = useRouter()
  const [checked, setChecked] = useState<boolean>(false)
  const [createContact, { loading }] = useCreateContactMutation({
    onCompleted: (data) => {
      if (!data.createContact.error) {
        router.push('/contact/complete')
      }
    },
  })
  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm<ContactFormData>({
    mode: 'all',
    resolver: zodResolver(schema),
    defaultValues: {
      name: '',
      email: '',
      tel: '',
      message: '',
    },
  })

  const onSubmit = (data: ContactFormData) => {
    createContact({
      variables: {
        uuid: process.env.NEXT_PUBLIC_BRAND_ID_CANADAGOOSE || '',
        attributes: data,
      },
    })
  }

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="flex flex-col justify-center mx-auto w-full font-base sm:w-[546px] px-8 md:px-0.5 gap-2">
          <div className="pt-12 sm:pt-4 text-center text-[28px] font-semibold lg:text-[34px] mb-[20px] md:mb-[60px]">
            お問い合わせ
          </div>
          <div className="flex flex-col gap-6 lg:gap-10">
            <div className="flex flex-col gap-4">
              <div>
                <span className="font-bold text-sm inline-block mb-2">
                  お名前<span className="text-[#FF0000]">*</span>
                </span>
                <Input
                  type="text"
                  placeholder="YAMADA HANAKO"
                  {...register('name')}
                  error={!!errors.name}
                  className="border border-[#C9C9C9] bg-[#FBFBFB] focus:border-black"
                />
                {errors.name && (
                  <span className="text-xs text-[#f00]">
                    {errors.name.message}
                  </span>
                )}
              </div>
              <div>
                <span className="font-bold text-sm inline-block mb-2">
                  メールアドレス<span className="text-[#FF0000]">*</span>
                </span>
                <Input
                  type="email"
                  placeholder="test@example.com"
                  {...register('email')}
                  error={!!errors.email}
                  className="border border-[#C9C9C9] bg-[#FBFBFB] focus:border-black"
                />
                {errors.email && (
                  <span className="text-xs text-[#f00]">
                    {errors.email.message}
                  </span>
                )}
              </div>
              <div>
                <span className="font-bold text-sm inline-block mb-2">
                  電話番号<span className="text-[#FF0000]">*</span>
                </span>
                <Input
                  required
                  type="text"
                  placeholder="09011112222"
                  {...register('tel')}
                  className="border border-[#C9C9C9] bg-[#FBFBFB] focus:border-black"
                />
                {errors.tel && (
                  <span className="text-xs text-[#f00]">
                    {errors.tel.message}
                  </span>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <div className="text-[16px] font-medium">
                  お問い合わせ内容 <span className="text-[#FF0000]">*</span>
                </div>
                <textarea
                  placeholder="お問い合わせ内容をご記入ください"
                  className="w-full h-[157px] px-[18px] py-[14px] text-[12px] rounded-[4px] overflow-y-auto overflow-x-hidden border border-[#C9C9C9] bg-[#FBFBFB] focus:border-black focus:outline-none focus:ring-0"
                  {...register('message')}
                />
                {errors.message && (
                  <span className="text-xs text-[#f00]">
                    {errors.message.message}
                  </span>
                )}
              </div>
              <div className="h-[163px] px-[24px] py-[24px] text-[12px] rounded-[4px] overflow-y-auto overflow-x-hidden mt-4 border border-[#C9C9C9]">
                {TEXT_DATA.map((item: DATA_TYPE, index: number) => {
                  return (
                    <div className="flex flex-col" key={index}>
                      <div className="text-[14px] lg:leading-7 leading-4 font-medium pb-[10px]">
                        {parse(item.title)}
                      </div>
                      <div className="text-[12px] leading-5 font-normal break-all pb-[10px]">
                        {parse(item.text)}
                      </div>
                    </div>
                  )
                })}
              </div>
              <div className="flex items-center space-x-2 justify-center mt-4">
                <Checkbox
                  id="privacy"
                  className="data-[state=checked]:bg-black data-[state=checked]:border-black bg-white border border-[#DDDFE1] rounded-none"
                  checked={checked}
                  onCheckedChange={(checked: boolean) => setChecked(checked)}
                />
                <label
                  htmlFor="privacy"
                  className="text-[12px] font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                >
                  プライバシーポリシーに同意する
                </label>
              </div>
            </div>
          </div>
          <div className="flex justify-center mt-7 mb-20 lg:mt-10 lg:mb-28">
            <Button
              type="submit"
              className="w-64 h-14 order-1 md:order-2 rounded-none bg-[#363636]"
              loading={loading}
              disabled={!isValid || !checked}
            >
              送信する
            </Button>
          </div>
        </div>
      </form>
    </>
  )
}

export default ContactPage
